import { Controller } from "@hotwired/stimulus"

/**
 * Enforces "either logo OR info title, not both" on the benefit form.
 * Disables (and excludes from submit) the other field when one is set;
 * re-enables when the other becomes blank. Also sets logo_alt required when logo is present.
 */
export default class extends Controller {
  static targets = ["logoAltAsterisk", "infoTitleAsterisk"]

  static values = {
    logoParamName: { type: String, default: "benefit[logo]" },
    infoTitleName: { type: String, default: "benefit[info_title]" },
    logoAltName: { type: String, default: "benefit[logo_alt]" }
  }

  connect() {
    this.logoInput = this.element.querySelector(`input[type="file"][name="${this.logoParamNameValue}"]`)
    this.infoTitleInput = this.element.querySelector(
      `input[name="${this.infoTitleNameValue}"], textarea[name="${this.infoTitleNameValue}"]`
    )
    this.logoAltInput = this.element.querySelector(`input[name="${this.logoAltNameValue}"]`)
    this.logoFieldAsteriskEl = this.element.querySelector("[data-dynamic-required-asterisk-for=\"logo\"]")
    if (!this.logoInput || !this.infoTitleInput) return

    this.logoPreviewData = this.element.querySelector("input[name=\"benefit[_logo_preview_data]\"]")
    this.logoOriginalData = this.element.querySelector("input[name=\"benefit[_logo_original_data]\"]")

    this.logoInput.addEventListener("change", this.boundOnLogoChange)
    this.infoTitleInput.addEventListener("input", this.boundOnInfoTitleInput)
    this.element.addEventListener("image-cropper-update", this.boundOnLogoUpdate)

    this.syncState()
  }

  get boundOnLogoChange() {
    if (!this._boundOnLogoChange) this._boundOnLogoChange = this.onLogoChange.bind(this)
    return this._boundOnLogoChange
  }

  get boundOnInfoTitleInput() {
    if (!this._boundOnInfoTitleInput) this._boundOnInfoTitleInput = this.onInfoTitleInput.bind(this)
    return this._boundOnInfoTitleInput
  }

  get boundOnLogoUpdate() {
    if (!this._boundOnLogoUpdate) this._boundOnLogoUpdate = this.onLogoUpdate.bind(this)
    return this._boundOnLogoUpdate
  }

  disconnect() {
    if (this.logoInput) this.logoInput.removeEventListener("change", this.boundOnLogoChange)
    if (this.infoTitleInput) this.infoTitleInput.removeEventListener("input", this.boundOnInfoTitleInput)
    this.element.removeEventListener("image-cropper-update", this.boundOnLogoUpdate)
  }

  onLogoChange() {
    this.syncState()
  }

  onLogoUpdate(event) {
    if (event.detail?.type === "logo") this.syncState()
  }

  onInfoTitleInput() {
    this.syncState()
  }

  hasLogo() {
    if (this.logoInput.files && this.logoInput.files.length > 0) return true
    if (this.logoPreviewData && this.logoPreviewData.value && this.logoPreviewData.value.trim() !== "")
      return true
    const logoPreview = this.element.querySelector('[data-image-cropper-target="logoPreview"]')
    const src = logoPreview?.src
    if (!src || src === "#") return false
    if (src.startsWith("data:") || src.startsWith("blob:")) return true
    if (src.startsWith("http") && !src.endsWith("#")) return true
    return false
  }

  hasInfoTitle() {
    return this.infoTitleInput.value.trim().length > 0
  }

  syncState() {
    const hasLogo = this.hasLogo()
    const hasInfoTitle = this.hasInfoTitle()

    this.infoTitleInput.disabled = !!hasLogo
    if (hasLogo) {
      this.infoTitleInput.removeAttribute("required")
      this.infoTitleInput.setAttribute("aria-optional", "true")
    } else {
      this.infoTitleInput.setAttribute("required", "required")
      this.infoTitleInput.removeAttribute("aria-optional")
    }
    this.logoInput.disabled = !!hasInfoTitle

    if (this.logoAltInput) {
      if (hasInfoTitle) {
        this.logoAltInput.disabled = true
        this.logoAltInput.removeAttribute("required")
        this.logoAltInput.setAttribute("aria-optional", "true")
      } else {
        this.logoAltInput.disabled = false
        if (hasLogo) {
          this.logoAltInput.setAttribute("required", "required")
          this.logoAltInput.removeAttribute("aria-optional")
        } else {
          this.logoAltInput.removeAttribute("required")
          this.logoAltInput.setAttribute("aria-optional", "true")
        }
      }
    }
    if (this.hasLogoAltAsteriskTarget) {
      const showLogoAltAsterisk = hasLogo && !hasInfoTitle
      this.logoAltAsteriskTarget.classList.toggle("d-none", !showLogoAltAsterisk)
    }
    if (this.hasInfoTitleAsteriskTarget) {
      const showInfoTitleAsterisk = !hasLogo
      this.infoTitleAsteriskTarget.classList.toggle("d-none", !showInfoTitleAsterisk)
    }
    if (this.logoFieldAsteriskEl) {
      const showLogoFieldAsterisk = !hasInfoTitle
      this.logoFieldAsteriskEl.classList.toggle("d-none", !showLogoFieldAsterisk)
    }
  }
}
